import { useAppSelector } from '@frontend/common';
import { isUUID } from '@frontend/components/common';
import { changeFilter, listStore, refresh, useListRepository } from '@frontend/repository';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { ListProps } from './list.component';

interface ViewProps<T extends object> extends ReturnType<typeof useListRepository<T>> {
    changeSelectedObjects: Dispatch<SetStateAction<T[]>>;
    selectedObjects: T[];
    filterKeys: string[];
    sortKeys: string[];
    nextPage?: () => void;
    loading: boolean;
}

const useList = <T extends { id: string }>(props: ListProps<T>): ViewProps<T> => {
    const repository = useListRepository<T>({
        fetch: (arg) => props.fetch(arg),
        dispatch: props.dispatch,
        eventListener: props.eventListener,
        defaultListValues: props.defaultListValues,
        listId: props.id
    });

    const [selectedObjects, changeSelectedObjects] = useState<T[]>([]);
    const [filterKeys, changeFilterKeys] = useState<string[]>([]);
    const [sortKeys, changeSortKeys] = useState<string[]>([]);

    useEffect(() => {
        changeSelectedObjects(repository.selected);
        props.selectedObjects && props.selectedObjects(repository.selected);
    }, [repository.selected]);

    useEffect(() => {
        if (repository.data) {
            const obj = repository.data[0];
            if (!obj) return;
            else {
                const filters: string[] = [];
                const sorting: string[] = [];
                Object.keys(obj).forEach((key) => {
                    if ((typeof (obj as any)[key] === 'string' || (obj as any)[key] === null) && (isUUID((obj as any)[key]) || key.includes('_id'))) {
                        if (key === 'account_id') return;
                        filters.push(key);
                    } else {
                        sorting.push(key);
                    }
                });
                changeFilterKeys(filters);
                changeSortKeys(sorting);
            }
        }
    }, [repository.data]);

    const store = useAppSelector(useSelector, listStore);
    const list = store[repository.listId];

    useEffect(() => {
        if (props.search !== undefined && repository.listId && list) {
            const searchValue = props.search === null || props.search.trim() === '' ? null : props.search.trim();
            props.dispatch(changeFilter({ listId: repository.listId, key: 'tracking_number', value: searchValue }));
            props.dispatch(refresh({ listId: repository.listId, refresh: true }));
        }
    }, [props.search, repository.listId, props.dispatch, list]);

    return {
        ...repository,
        selectedObjects,
        changeSelectedObjects,
        filterKeys,
        sortKeys,
        loading: repository.data === null
    };
};

export default useList;
